import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClientProvider } from '@tanstack/react-query';
import theme from './theme';
import './App.css';
import { AuthProvider } from './auth/AuthContext.jsx';
import RequireAuth from './components/RouteGuards/RequireAuth.jsx';
import RequireAdmin from './components/RouteGuards/RequireAdmin.jsx';
import { FeedbackProvider } from './ui/FeedbackProvider';
import CommandPalette from './ui/CommandPalette';
import PageSkeleton from './ui/PageSkeleton';
import queryClient from './queryClient';

const Auth = lazy(() => import('./pages/Auth/Auth.jsx'));
const Signup = lazy(() => import('./pages/Signup/Signup.jsx'));
const NewCustomerSignup = lazy(() => import('./pages/Signup/NewCustomerSignup.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard.jsx'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard/AdminDashboard.jsx'));
const PipelineDashboard = lazy(() => import('./pages/AdminDashboard/PipelineDashboard.jsx'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails/ProjectDetails.jsx'));
const Settings = lazy(() => import('./pages/Settings/Settings.jsx'));
const CustomerActivity = lazy(() => import('./pages/Customers/Customers.jsx'));
const CustomerEdit = lazy(() => import('./pages/Customers/CustomerEdit.jsx'));
const Estimates = lazy(() => import('./pages/Estimates/Estimates.jsx'));
const EstimatesBoard = lazy(() => import('./pages/Estimates/EstimatesBoard.jsx'));
const EstimateEdit = lazy(() => import('./pages/Estimates/EstimateEdit.jsx'));
const EstimateView = lazy(() => import('./pages/Estimates/EstimateView.jsx'));
const Contracts = lazy(() => import('./pages/Contracts/Contracts.jsx'));
const ContractsBoard = lazy(() => import('./pages/Contracts/ContractsBoard.jsx'));
const ContractEdit = lazy(() => import('./pages/Contracts/ContractEdit.jsx'));
const ContractView = lazy(() => import('./pages/Contracts/ContractView.jsx'));
const AmendmentEdit = lazy(() => import('./pages/Amendments/AmendmentEdit.jsx'));
const AmendmentView = lazy(() => import('./pages/Amendments/AmendmentView.jsx'));
const PICRAUpload = lazy(() => import('./pages/PICRAUpload/PICRAUpload.jsx'));
const MyDocuments = lazy(() => import('./pages/MyDocuments/MyDocuments.jsx'));
const InspectionReportInput = lazy(() => import('./pages/InspectionReport/InspectionReportInput.jsx'));
const PreWorkInspectionInput = lazy(() => import('./pages/PreWorkInspection/PreWorkInspectionInput.jsx'));
const PreWorkInspections = lazy(() => import('./pages/PreWorkInspection/PreWorkInspections.jsx'));
const ClientReportView = lazy(() => import('./pages/ClientReports/ClientReportView.jsx'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound.jsx'));
const AdminInbox = lazy(() => import('./pages/AdminInbox/AdminInbox.jsx'));
const AdminUsers = lazy(() => import('./pages/AdminUsers/AdminUsers.jsx'));
const PlaceholderPage = lazy(() => import('./pages/PlaceholderPage.jsx'));

const memberRoutes = [
  ['/dashboard', <Dashboard />],
  ['/project-dashboard', <Navigate to="/dashboard" replace />],
  ['/project-details/:projectId', <ProjectDetails />],
  ['/project-details', <ProjectDetails />],
  ['/settings', <Settings />],
  ['/my-documents', <MyDocuments />],
  ['/payments', <PlaceholderPage title="Payments" message="Client payments and invoices will live here. Use Documents for current files." />],
  ['/messages', <PlaceholderPage title="Messages" message="Job messaging arrives in Phase 3. Email the office for now." />],
  ['/client-reports/:reportId', <ClientReportView />],
];

const adminRoutes = [
  ['/admin', <AdminDashboard />],
  ['/admin/pipeline', <PipelineDashboard />],
  ['/admin/inbox', <AdminInbox />],
  ['/admin/users', <AdminUsers />],
  ['/admin/schedule', <PlaceholderPage title="Schedule" message="Crew calendar is Phase 3. Jobs with start dates are on the Jobs board." />],
  ['/admin/billing', <PlaceholderPage title="Billing" message="Open a job and use the Billing tab for invoices and receipts." />],
  ['/upload', <PICRAUpload />],
  ['/picra-upload', <PICRAUpload />],
  ['/customers', <CustomerActivity />],
  ['/customers/new', <CustomerEdit />],
  ['/customers/edit/:id', <CustomerEdit />],
  ['/estimates', <Estimates />],
  ['/estimates/board', <EstimatesBoard />],
  ['/estimates/new', <EstimateEdit />],
  ['/estimates/:id', <EstimateView />],
  ['/estimates/edit/:id', <EstimateEdit />],
  ['/contracts', <Contracts />],
  ['/jobs', <Navigate to="/contracts" replace />],
  ['/contracts/board', <ContractsBoard />],
  ['/contracts/new', <ContractEdit />],
  ['/contracts/:id', <ContractView />],
  ['/contracts/edit/:id', <ContractEdit />],
  ['/amendments/create', <AmendmentEdit />],
  ['/amendments/:id', <AmendmentView />],
  ['/amendments/edit/:id', <AmendmentEdit />],
  ['/inspection-report/:contractId', <InspectionReportInput />],
  ['/inspection-report/edit', <InspectionReportInput />],
  ['/prework-inspection/:contractId', <PreWorkInspectionInput />],
  ['/prework-inspection/edit', <PreWorkInspectionInput />],
  ['/prework-inspections', <PreWorkInspections />],
];

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <GoogleOAuthProvider clientId={googleClientId}>
          <AuthProvider>
            <FeedbackProvider>
              <Router>
                <div className="App">
                  <CommandPalette />
                  <Suspense fallback={<PageSkeleton />}>
                    <Routes>
                      <Route path="/login" element={<Auth />} />
                      <Route path="/auth" element={<Navigate to="/login" replace />} />
                      <Route path="/signup" element={<NewCustomerSignup />} />
                      <Route path="/signup-legacy" element={<Signup />} />
                      <Route path="/admin/quotes/*" element={<RequireAdmin><Navigate to="/estimates" replace /></RequireAdmin>} />

                      {memberRoutes.map(([path, element]) => (
                        <Route key={path} path={path} element={<RequireAuth>{element}</RequireAuth>} />
                      ))}

                      {adminRoutes.map(([path, element]) => (
                        <Route key={path} path={path} element={<RequireAdmin>{element}</RequireAdmin>} />
                      ))}

                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </div>
              </Router>
            </FeedbackProvider>
          </AuthProvider>
        </GoogleOAuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
